import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import OlMap from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import LineString from "ol/geom/LineString";
import { Circle as CircleStyle, Fill, Icon, Stroke, Style } from "ol/style";
import { fromLonLat } from "ol/proj";
import { useGlobalSettings } from "../../context/GlobalSettingsContext";
import {
  buildDriverMarkerStyleSet,
  createDriverMarkerStyles,
  resolveDriverMarkerState,
} from "../../utils/driverMapMarkers";
import { createHereTileSource, normalizeHereLanguage } from "../../utils/hereMaps";
import "ol/ol.css";

const DEFAULT_HEIGHT = 400;

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toCoordinate = (point) => {
  const latitude = toFiniteNumber(point?.latitude ?? point?.lat);
  const longitude = toFiniteNumber(point?.longitude ?? point?.lng);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return { latitude, longitude };
};

function buildMarkerStyle({ icon, scale = 1, color = "#2563eb" }) {
  if (icon) {
    return new Style({
      image: new Icon({
        src: icon,
        anchor: [0.5, 1],
        anchorXUnits: "fraction",
        anchorYUnits: "fraction",
        crossOrigin: "anonymous",
        scale: Math.max(0.2, Number(scale) || 1),
      }),
      zIndex: 20,
    });
  }

  return new Style({
    image: new CircleStyle({
      radius: 6,
      fill: new Fill({ color }),
      stroke: new Stroke({ color: "#ffffff", width: 2 }),
    }),
    zIndex: 20,
  });
}

function MapWithLayers({ latitude, longitude, layers = [], height = DEFAULT_HEIGHT }) {
  const hostRef = useRef(null);
  const mapRef = useRef(null);
  const vectorSourceRef = useRef(null);
  const latitudeRef = useRef(toFiniteNumber(latitude));
  const longitudeRef = useRef(toFiniteNumber(longitude));
  const hasAppliedInitialFitRef = useRef(false);
  const { t, i18n } = useTranslation();
  const { hereApiKey } = useGlobalSettings();

  const mapLanguage = useMemo(
    () => normalizeHereLanguage(i18n?.resolvedLanguage || i18n?.language || "en"),
    [i18n?.resolvedLanguage, i18n?.language]
  );
  const driverMarkerStyles = useMemo(() => createDriverMarkerStyles(), []);

  useEffect(() => {
    latitudeRef.current = toFiniteNumber(latitude);
    longitudeRef.current = toFiniteNumber(longitude);
  }, [latitude, longitude]);

  useEffect(() => {
    if (!hostRef.current || mapRef.current) return;

    const baseLayer = new TileLayer({
      source: createHereTileSource({
        apiKey: hereApiKey,
        language: mapLanguage,
        style: "lite.day",
        imageFormat: "png8",
        includeLanguage: true,
        disablePois: true,
      }),
      visible: true,
    });

    const vectorSource = new VectorSource();
    const vectorLayer = new VectorLayer({
      source: vectorSource,
      declutter: true,
    });

    const initialCenter = Number.isFinite(latitudeRef.current) && Number.isFinite(longitudeRef.current)
      ? fromLonLat([longitudeRef.current, latitudeRef.current])
      : fromLonLat([2.1734, 41.3851]);

    const map = new OlMap({
      target: hostRef.current,
      layers: [baseLayer, vectorLayer],
      view: new View({
        center: initialCenter,
        zoom: 12,
        minZoom: 2,
        maxZoom: 22,
      }),
    });

    const updateMapSize = () => map.updateSize();
    updateMapSize();
    const resizeRafId = window.requestAnimationFrame(updateMapSize);
    const resizeTimeoutId = window.setTimeout(updateMapSize, 120);
    window.addEventListener("resize", updateMapSize);

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => map.updateSize());
      resizeObserver.observe(hostRef.current);
    }

    mapRef.current = map;
    vectorSourceRef.current = vectorSource;

    return () => {
      window.removeEventListener("resize", updateMapSize);
      window.cancelAnimationFrame(resizeRafId);
      window.clearTimeout(resizeTimeoutId);
      if (resizeObserver) resizeObserver.disconnect();
      map.setTarget(undefined);
      mapRef.current = null;
      vectorSourceRef.current = null;
      hasAppliedInitialFitRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    const tileLayer = mapRef.current.getLayers().item(0);
    tileLayer?.setSource(
      createHereTileSource({
        apiKey: hereApiKey,
        language: mapLanguage,
        style: "lite.day",
        imageFormat: "png8",
        includeLanguage: true,
        disablePois: true,
      })
    );
    mapRef.current.render();
  }, [hereApiKey, mapLanguage]);

  useEffect(() => {
    const map = mapRef.current;
    const source = vectorSourceRef.current;
    if (!map || !source) return;

    source.clear();
    const features = [];

    layers.forEach((layer) => {
      if (layer?.type === "polyline") {
        const coordinates = (Array.isArray(layer.data) ? layer.data : [])
          .map((point) => toCoordinate(point))
          .filter(Boolean)
          .map(({ latitude: lat, longitude: lng }) => fromLonLat([lng, lat]));
        if (coordinates.length < 2) return;

        const feature = new Feature({ geometry: new LineString(coordinates) });
        feature.setStyle(
          new Style({
            stroke: new Stroke({
              color: layer.color || "#2563eb",
              width: Number(layer.width) || 4,
            }),
            zIndex: 10,
          })
        );
        features.push(feature);
      }

      if (layer?.type === "marker") {
        const dataPoints = Array.isArray(layer.data) ? layer.data : [];
        dataPoints.forEach((point) => {
          const coordinate = toCoordinate(point);
          if (!coordinate) return;
          const markerFeature = new Feature({
            geometry: new Point(fromLonLat([coordinate.longitude, coordinate.latitude])),
          });
          const markerKind = String(point.markerKind ?? layer.markerKind ?? "").toLowerCase();
          if (markerKind === "driver") {
            const driverState = resolveDriverMarkerState(point, layer);
            markerFeature.setStyle(buildDriverMarkerStyleSet(driverMarkerStyles, driverState));
          } else {
            markerFeature.setStyle(
              buildMarkerStyle({
                icon: point.icon || layer.icon,
                scale: point.scale || layer.scale || 1,
                color: layer.color || "#2563eb",
              })
            );
          }
          features.push(markerFeature);
        });
      }
    });

    if (features.length) {
      source.addFeatures(features);
      if (!hasAppliedInitialFitRef.current) {
        const extent = source.getExtent();
        if (extent && extent.every(Number.isFinite)) {
          map.getView().fit(extent, {
            padding: [40, 40, 40, 40],
            duration: 220,
            maxZoom: 17,
          });
          hasAppliedInitialFitRef.current = true;
        }
      }
      return;
    }

    if (!hasAppliedInitialFitRef.current) {
      const fallbackLat = latitudeRef.current;
      const fallbackLng = longitudeRef.current;
      if (Number.isFinite(fallbackLat) && Number.isFinite(fallbackLng)) {
        map.getView().animate({
          center: fromLonLat([fallbackLng, fallbackLat]),
          zoom: 13,
          duration: 200,
        });
      }
    }
  }, [layers, driverMarkerStyles]);

  const normalizedHeight = Number.isFinite(Number(height)) ? Number(height) : DEFAULT_HEIGHT;

  return (
    <div
      ref={hostRef}
      style={{ width: "100%", height: `${normalizedHeight}px`, borderRadius: 8, overflow: "hidden" }}
      aria-label={t("common.map", { defaultValue: "Map" })}
    />
  );
}

export default MapWithLayers;
